import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { MultiSelect } from '@/components/ui/multi-select';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import { 
  X, 
  Plus, 
  Settings, 
  Building2, 
  Clock,
  Calendar,
  Repeat
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function MaintenanceTaskForm({ type, frequency, onClose, onCreate, user }) {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    title: '',
    department: '',
    machine: '',
    assignees: [],  // Changed from single to array
    frequency: frequency || 'Daily',
    // For daily: no date needed, just time
    // For weekly: day of week (Monday-Sunday)
    // For monthly: period (start/middle/end of month)
    // For yearly: month of year (January-December)
    dayOfWeek: 'Monday', // For weekly tasks
    monthlyPeriod: 'start', // For monthly tasks: 'start', 'middle', 'end'
    yearlyMonth: 'January', // For yearly tasks: month selection
    time: '08:00',
    priority: 'Medium',
    notes: '',
    safety_notes: ''
  });
  const [checklistItems, setChecklistItems] = useState([]);
  const [newChecklistItem, setNewChecklistItem] = useState('');
  const [departments, setDepartments] = useState([]);
  const [machines, setMachines] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchDepartments();
    fetchUsers();
  }, []);

  useEffect(() => {
    if (formData.department && formData.department !== 'ALL_DEPARTMENTS') {
      fetchMachines(formData.department);
    } else if (formData.department === 'ALL_DEPARTMENTS') {
      // For "All Departments", we don't fetch specific machines
      setMachines([]);
      setFormData(prev => ({ ...prev, machine: 'ALL_MACHINES' }));
    } else {
      setMachines([]);
      setFormData(prev => ({ ...prev, machine: '' }));
    }
  }, [formData.department]);

  const fetchDepartments = async () => {
    try {
      const response = await axios.get(`${API}/departments`);
      setDepartments(response.data);
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  const fetchMachines = async (departmentId) => {
    try {
      const response = await axios.get(`${API}/machines?department_id=${departmentId}`);
      setMachines(response.data);
    } catch (error) {
      console.error('Error fetching machines:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/assignees`);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching assignees:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.department || !formData.machine) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      // Create work order via API (same as repair tasks)
      const workOrderData = {
        title: formData.title,
        type: 'PM', // Preventive Maintenance type
        priority: formData.priority,
        assignees: formData.assignees.filter(id => id !== 'none'),  // Multiple assignees
        department_id: formData.department,
        machine_id: formData.machine,
        description: formData.notes || '',
        checklist_items: checklistItems,
        frequency: formData.frequency,
        day_of_week: formData.dayOfWeek,
        monthly_period: formData.monthlyPeriod,
        yearly_month: formData.yearlyMonth,
        time: formData.time,
        notes: formData.notes,
        safety_notes: formData.safety_notes,
        // Flag to indicate this is for all departments/machines
        is_all_departments: formData.department === 'ALL_DEPARTMENTS',
        is_all_machines: formData.machine === 'ALL_MACHINES'
      };

      console.log('Creating maintenance work order via API:', workOrderData);

      const response = await axios.post(`${API}/work-orders`, workOrderData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      console.log('Maintenance work order created:', response.data);

      // Dispatch event to notify Calendar and DailyTasks
      const createEvent = new CustomEvent('workOrderUpdated', {
        detail: response.data  // Send the complete new task data
      });
      window.dispatchEvent(createEvent);

      toast.success('Maintenance task created successfully!');
      
      // Call onCreate and close modal
      try {
        onCreate(response.data);
      } catch (callbackError) {
        console.error('Error in onCreate callback:', callbackError);
      }
      
      onClose();
    } catch (error) {
      console.error('Failed to create maintenance task:', error);
      toast.error('Failed to create maintenance task');
    } finally {
      setLoading(false);
    }
  };

  const addChecklistItem = () => {
    if (!newChecklistItem.trim()) return;
    
    setChecklistItems(prev => [...prev, newChecklistItem.trim()]);
    setNewChecklistItem('');
  };

  const removeChecklistItem = (index) => {
    setChecklistItems(prev => prev.filter((_, i) => i !== index));
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const getFrequencyIcon = (freq) => {
    const icons = {
      'Daily': Clock,
      'Weekly': Calendar,
      'Monthly': Repeat
    };
    return icons[freq] || Clock;
  };

  const FrequencyIcon = getFrequencyIcon(formData.frequency);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-hidden bg-white">
        <CardHeader className="pb-4 border-b">
          <div className="flex items-start justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg flex items-center justify-center">
                <FrequencyIcon className="w-6 h-6 text-white" />
              </div>
              <div>
                <CardTitle className="text-xl text-gray-900">
                  {t('create')} {t(formData.frequency.toLowerCase())} {t('maintenanceWorkOrders')}
                </CardTitle>
                <p className="text-sm text-gray-600 mt-1">
                  {t('addNewPreventiveMaintenance')}
                </p>
              </div>
            </div>
            
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="h-8 w-8 p-0"
              data-testid="close-maintenance-form"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>

        <div className="overflow-y-auto max-h-[calc(90vh-120px)]">
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 border-b pb-2">{t('basicInformation')}</h3>
                
                <div>
                  <Label htmlFor="task-title" className="text-sm font-medium text-gray-700">
                    {t('taskTitleRequired')}
                  </Label>
                  <Input
                    id="task-title"
                    type="text"
                    placeholder={t('enterWorkOrderTitle')}
                    value={formData.title}
                    onChange={(e) => handleChange('title', e.target.value)}
                    required
                    className="mt-1"
                    data-testid="maintenance-task-title"
                  />
                </div>

                <div>
                  <Label className="text-sm font-medium text-gray-700">{t('frequencyRequired')}</Label>
                  <Select 
                    value={formData.frequency} 
                    onValueChange={(value) => handleChange('frequency', value)}
                  >
                    <SelectTrigger className="mt-1" data-testid="maintenance-task-frequency">
                      <SelectValue placeholder={t('frequency')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Daily">{t('daily')}</SelectItem>
                      <SelectItem value="Weekly">{t('weekly')}</SelectItem>
                      <SelectItem value="Monthly">{t('monthly')}</SelectItem>
                      <SelectItem value="Yearly">{t('yearly')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium text-gray-700">{t('departmentRequired')}</Label>
                    <Select 
                      value={formData.department} 
                      onValueChange={(value) => handleChange('department', value)}
                    >
                      <SelectTrigger className="mt-1" data-testid="maintenance-task-department">
                        <SelectValue placeholder={t('selectDepartment')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ALL_DEPARTMENTS">
                          <span className="font-semibold text-blue-600">🏢 All Departments</span>
                        </SelectItem>
                        {departments.map(dept => (
                          <SelectItem key={dept.id} value={dept.id}>
                            {dept.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="text-sm font-medium text-gray-700">{t('machineEquipmentRequired')}</Label>
                    <Select 
                      value={formData.machine} 
                      onValueChange={(value) => handleChange('machine', value)}
                      disabled={!formData.department}
                    >
                      <SelectTrigger className="mt-1" data-testid="maintenance-task-machine">
                        <SelectValue placeholder={formData.department ? t('selectMachine') : t('selectDepartmentFirst')} />
                      </SelectTrigger>
                      <SelectContent>
                        {formData.department === 'ALL_DEPARTMENTS' ? (
                          <SelectItem value="ALL_MACHINES">
                            <span className="font-semibold text-blue-600">⚙️ All Machines</span>
                          </SelectItem>
                        ) : (
                          <>
                            <SelectItem value="ALL_MACHINES">
                              <span className="font-semibold text-blue-600">⚙️ All Machines</span>
                            </SelectItem>
                            {machines.map(machine => (
                              <SelectItem key={machine.id} value={machine.id}>
                                {machine.name}
                              </SelectItem>
                            ))}
                          </>
                        )}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="text-sm font-medium text-gray-700">{t('assignee')} (Multiple)</Label>
                    <MultiSelect
                      options={users.map(u => ({ value: u.id, label: u.name || u.username }))}
                      value={formData.assignees}
                      onChange={(selectedIds) => handleChange('assignees', selectedIds)}
                      placeholder="Select assignees..."
                      className="mt-1"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Conditional field based on frequency */}
                  {formData.frequency === 'Weekly' && (
                    <div>
                      <Label htmlFor="day-of-week" className="text-sm font-medium text-gray-700">
                        {t('dayOfWeek')}
                      </Label>
                      <Select 
                        value={formData.dayOfWeek} 
                        onValueChange={(value) => handleChange('dayOfWeek', value)}
                      >
                        <SelectTrigger className="mt-1" id="day-of-week">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Monday">{t('monday')}</SelectItem>
                          <SelectItem value="Tuesday">{t('tuesday')}</SelectItem>
                          <SelectItem value="Wednesday">{t('wednesday')}</SelectItem>
                          <SelectItem value="Thursday">{t('thursday')}</SelectItem>
                          <SelectItem value="Friday">{t('friday')}</SelectItem>
                          <SelectItem value="Saturday">{t('saturday')}</SelectItem>
                          <SelectItem value="Sunday">{t('sunday')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {formData.frequency === 'Monthly' && (
                    <div>
                      <Label htmlFor="monthly-period" className="text-sm font-medium text-gray-700">
                        {t('periodOfMonth')}
                      </Label>
                      <Select 
                        value={formData.monthlyPeriod} 
                        onValueChange={(value) => handleChange('monthlyPeriod', value)}
                      >
                        <SelectTrigger className="mt-1" id="monthly-period">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="start">Start of Month (Mon-Fri)</SelectItem>
                          <SelectItem value="middle">Middle of Month (Mon-Fri)</SelectItem>
                          <SelectItem value="end">End of Month (Mon-Fri)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Month selection for Yearly tasks */}
                  {formData.frequency === 'Yearly' && (
                    <div>
                      <Label className="text-sm font-medium text-gray-700">Month</Label>
                      <Select 
                        value={formData.yearlyMonth} 
                        onValueChange={(value) => handleChange('yearlyMonth', value)}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Select month" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="January">January</SelectItem>
                          <SelectItem value="February">February</SelectItem>
                          <SelectItem value="March">March</SelectItem>
                          <SelectItem value="April">April</SelectItem>
                          <SelectItem value="May">May</SelectItem>
                          <SelectItem value="June">June</SelectItem>
                          <SelectItem value="July">July</SelectItem>
                          <SelectItem value="August">August</SelectItem>
                          <SelectItem value="September">September</SelectItem>
                          <SelectItem value="October">October</SelectItem>
                          <SelectItem value="November">November</SelectItem>
                          <SelectItem value="December">December</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <div>
                    <Label htmlFor="task-time" className="text-sm font-medium text-gray-700">
                      Scheduled Time
                    </Label>
                    <Input
                      id="task-time"
                      type="time"
                      value={formData.time}
                      onChange={(e) => handleChange('time', e.target.value)}
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label className="text-sm font-medium text-gray-700">Priority</Label>
                    <Select 
                      value={formData.priority} 
                      onValueChange={(value) => handleChange('priority', value)}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Low">Low</SelectItem>
                        <SelectItem value="Medium">Medium</SelectItem>
                        <SelectItem value="High">High</SelectItem>
                        <SelectItem value="Critical">Critical</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Instructions */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 border-b pb-2">Instructions</h3>
                
                <div>
                  <Label htmlFor="task-notes" className="text-sm font-medium text-gray-700">
                    Maintenance Instructions
                  </Label>
                  <Textarea
                    id="task-notes"
                    placeholder="Detailed instructions for performing this maintenance task..."
                    value={formData.notes}
                    onChange={(e) => handleChange('notes', e.target.value)}
                    rows={4}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="task-safety" className="text-sm font-medium text-gray-700">
                    Safety Notes
                  </Label>
                  <Textarea
                    id="task-safety"
                    placeholder="Important safety considerations and precautions..."
                    value={formData.safety_notes}
                    onChange={(e) => handleChange('safety_notes', e.target.value)}
                    rows={3}
                    className="mt-1"
                  />
                </div>
              </div>

              {/* Checklist */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 border-b pb-2">Checklist Items</h3>
                
                <div className="flex space-x-2">
                  <Input
                    type="text"
                    placeholder="Add checklist item (e.g., Check oil levels)"
                    value={newChecklistItem}
                    onChange={(e) => setNewChecklistItem(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addChecklistItem())}
                    className="flex-1"
                    data-testid="new-maintenance-task-checklist-item"
                  />
                  <Button
                    type="button"
                    onClick={addChecklistItem}
                    disabled={!newChecklistItem.trim()}
                    size="sm"
                    data-testid="add-maintenance-task-checklist-item"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>

                {checklistItems.length > 0 && (
                  <div className="space-y-2 max-h-32 overflow-y-auto">
                    {checklistItems.map((item, index) => (
                      <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                        <span className="text-sm text-gray-700">{item}</span>
                        <Button
                          type="button"
                          onClick={() => removeChecklistItem(index)}
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 text-red-600 hover:text-red-700"
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Form Actions */}
              <div className="flex justify-end space-x-3 pt-6 border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onClose}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={loading || !formData.title.trim() || !formData.department || !formData.machine}
                  className="bg-green-600 hover:bg-green-700 text-white"
                  data-testid="create-maintenance-task-btn"
                >
                  {loading ? (
                    <div className="flex items-center space-x-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Creating...</span>
                    </div>
                  ) : (
                    <>
                      <Plus className="w-4 h-4 mr-2" />
                      Create Task
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </div>
      </Card>
    </div>
  );
}

export default MaintenanceTaskForm;